"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, ButtonGroup, Flex, Heading, IconButton, Spinner, Text, Tooltip, useToast } from "@chakra-ui/react";
import { DownloadIcon, InfoIcon } from "@chakra-ui/icons";
import axios from "axios";
import ExcelJS from "exceljs";
import echarts from "./lib";

// ABC SKU Segmentation — record: backend docs/michael/CHANGES-2026-09-16-abc-segmentation.md
//
// The API returns EVERY article of the supplier for the last 12 months, biggest
// first, already banded by the standard ABC rule (A = the articles that together
// make the first 80% of sales, B = the next 15%, C = the last 5% + no sales) plus
// a `meta` block (window, total, per-band counts). This component only slices:
//   Top 10 best   → the first 10 rows as bars
//   Top 10 worst  → the last 10 rows (zero sellers first) as bars
//   All           → the whole list, grouped by band, scrollable, with Excel download
// The chosen view is remembered per user in localStorage.

// Semantic ABC colours (Chakra green.500 / orange.400 / red.500).
const SEGMENT_COLORS = { A: "#38A169", B: "#ED8936", C: "#E53E3E" };
const SEGMENT_TITLE = { A: "A · first 80% of sales", B: "B · next 15%", C: "C · last 5% and no sales" };
const SEGMENT_DESC = {
  A: "one of the articles that together make the first 80% of sales",
  B: "in the next 15% of sales",
  C: "in the last 5% of sales, or no sales",
};

const VIEWS = [
  { key: "best", label: "Top 10 best" },
  { key: "worst", label: "Top 10 worst" },
  { key: "all", label: "All" },
];
const viewStorageKey = () => `spearbi_abc_view:${localStorage.getItem("userid") || "anon"}`;
const readView = () => {
  try {
    const v = localStorage.getItem(viewStorageKey());
    return VIEWS.some((x) => x.key === v) ? v : "best";
  } catch (e) {
    return "best";
  }
};

const fmtR = (v) => `R${Math.round(Number(v) || 0).toLocaleString("en-US")}`;
const fmtShort = (value) => {
  const v = Number(value) || 0;
  if (v >= 1000000) return `R${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `R${(v / 1000).toFixed(0)}K`;
  return `R${Math.round(v).toLocaleString("en-US")}`;
};
const fmtPct = (x) => `${((Number(x) || 0) * 100).toFixed(1)}%`;

const barGradient = (base) => ({
  type: "linear",
  x: 0,
  y: 0,
  x2: 1,
  y2: 0,
  colorStops: [
    { offset: 0, color: base + "66" }, // ~40% alpha at the base
    { offset: 1, color: base },
  ],
});

// Backend older than this component → no meta block; derive the band counts
// from the rows so the legend still reads correctly.
const deriveMeta = (rows, meta) => {
  if (meta && meta.bands) return meta;
  const bands = { A: { count: 0, value: 0, share: 0 }, B: { count: 0, value: 0, share: 0 }, C: { count: 0, value: 0, share: 0 } };
  let total = 0;
  rows.forEach((r) => {
    const v = Math.max(0, Number(r.value) || 0);
    total += v;
    const b = bands[r.segment] || bands.C;
    b.count += 1;
    b.value += v;
  });
  Object.values(bands).forEach((b) => {
    b.share = total ? b.value / total : 0;
  });
  return { total, start: null, end: null, articles: rows.length, bands };
};

const Chart10 = ({ supplier, retailer }) => {
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const bandRefs = useRef({});
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [view, setView] = useState(readView);

  const changeView = (key) => {
    setView(key);
    try {
      localStorage.setItem(viewStorageKey(), key);
    } catch (e) {
      /* storage blocked — the choice simply is not remembered */
    }
  };

  // ── data ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let disposed = false;
    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/rep_sales/chart10`, {
        supplier,
        retailer,
        userid: localStorage.getItem("userid"),
      })
      .then((res) => {
        if (disposed) return;
        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        setRows(data);
        setMeta(deriveMeta(data, res.data?.meta));
      })
      .catch((err) => {
        console.error("Chart10 fetch error", err);
        if (!disposed) {
          setRows([]);
          setMeta(null);
        }
      })
      .finally(() => {
        if (!disposed) setLoading(false);
      });
    return () => {
      disposed = true;
    };
  }, [supplier, retailer]);

  const bands = (meta && meta.bands) || {};

  // rows arrive biggest-first
  const shown = useMemo(() => {
    if (view === "best") return rows.slice(0, 10);
    if (view === "worst") return [...rows].reverse().slice(0, 10);
    return rows;
  }, [rows, view]);

  // ── bars (best / worst) ─────────────────────────────────────────────────
  useEffect(() => {
    if (view === "all" || !containerRef.current || !shown.length) return undefined;
    const el = containerRef.current;
    const data = shown;
    el.style.height = `${Math.max(data.length * 28, 120)}px`;

    const chart = echarts.init(el);
    chart.setOption({
      grid: { left: 8, right: 96, top: 6, bottom: 4, containLabel: true },
      xAxis: {
        type: "value",
        axisLabel: { color: "#64798A", fontSize: 10.5, formatter: fmtShort },
        splitLine: { lineStyle: { color: "#E2E9EE", type: [3, 3] } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: "category",
        inverse: true,
        data: data.map((d) => d.sku),
        axisLabel: {
          color: "#64798A",
          fontSize: 10.5,
          fontFamily: "Inter, sans-serif",
          width: 170,
          overflow: "truncate",
        },
        axisLine: { show: false },
        axisTick: { show: false },
        triggerEvent: true,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow", shadowStyle: { color: "rgba(29,47,58,0.05)" } },
        confine: true,
        backgroundColor: "#ffffff",
        borderColor: "#E2E9EE",
        borderWidth: 1,
        padding: [8, 12],
        textStyle: { color: "#1D2F3A", fontSize: 12 },
        extraCssText: "box-shadow: 0 6px 18px rgba(29,47,58,0.14); border-radius: 8px;",
        formatter: (params) => {
          const p = params[0];
          const row = data[p.dataIndex];
          if (!row) return "";
          const segColor = SEGMENT_COLORS[row.segment] || "#64798A";
          const inactive = row.active === false ? `<br/><i>inactive${row.inactive_since ? ` since ${row.inactive_since}` : ""}</i>` : "";
          return (
            `${row.sku}<br/>` +
            `<b>${fmtR(row.value)}</b> · ${fmtPct(row.share)} of sales<br/>` +
            `Rank ${row.rank} of ${rows.length} · running total ${fmtPct(row.cumulative)}<br/>` +
            `<span style="color:${segColor};font-weight:600">${row.segment}</span> — ${SEGMENT_DESC[row.segment] || ""}${inactive}`
          );
        },
      },
      series: [
        {
          type: "bar",
          barWidth: "55%",
          showBackground: true,
          backgroundStyle: { color: "#F6F8FA", borderRadius: [0, 5, 5, 0] },
          data: data.map((d) => ({
            value: Math.max(0, Number(d.value) || 0),
            itemStyle: {
              borderRadius: [0, 5, 5, 0],
              color: barGradient(SEGMENT_COLORS[d.segment] || "#64798A"),
            },
          })),
          label: {
            show: true,
            position: "right",
            distance: 6,
            formatter: (p) => {
              const r = data[p.dataIndex];
              return r ? `${fmtShort(r.value)} · ${fmtPct(r.share)}` : "";
            },
            color: "#4A5D6B",
            fontSize: 10.5,
            fontWeight: 700,
            fontFamily: "Inter, sans-serif",
          },
          labelLayout: { hideOverlap: true },
          emphasis: {
            itemStyle: { shadowBlur: 10, shadowColor: "rgba(29,47,58,0.22)" },
          },
          animationDuration: 700,
          animationDelay: (idx) => idx * 25,
          animationEasing: "cubicOut",
        },
      ],
    });

    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(el);
    return () => {
      ro.disconnect();
      chart.dispose();
    };
  }, [shown, view, rows.length]);

  // ── "All" helpers ───────────────────────────────────────────────────────
  const jumpToBand = (seg) => {
    const list = listRef.current;
    const el = bandRefs.current[seg];
    if (list && el) list.scrollTo({ top: el.offsetTop, behavior: "smooth" });
  };

  const download = async () => {
    if (!rows.length) return;
    setDownloading(true);
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("ABC Segmentation");
      ws.columns = [
        { header: "Rank", key: "rank", width: 7 },
        { header: "Band", key: "segment", width: 7 },
        { header: "Article Code", key: "articlecode", width: 16 },
        { header: "Description", key: "sku", width: 48 },
        { header: "Sales (R)", key: "value", width: 14 },
        { header: "Units", key: "units", width: 10 },
        { header: "Share of Sales", key: "share", width: 14 },
        { header: "Running Total", key: "cumulative", width: 14 },
        { header: "Status", key: "status", width: 24 },
      ];
      rows.forEach((r) => {
        ws.addRow({
          rank: r.rank,
          segment: r.segment,
          articlecode: r.articlecode || "",
          sku: r.sku,
          value: Number(r.value) || 0,
          units: Number(r.units) || 0,
          share: Number(r.share) || 0,
          cumulative: Number(r.cumulative) || 0,
          status: r.active === false ? `Inactive${r.inactive_since ? ` since ${r.inactive_since}` : ""}` : "Active",
        });
      });
      ws.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
      ws.getRow(1).eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58B6C0" } };
      });
      ws.getColumn("value").numFmt = "#,##0";
      ws.getColumn("units").numFmt = "#,##0";
      ws.getColumn("share").numFmt = "0.0%";
      ws.getColumn("cumulative").numFmt = "0.0%";
      const ARGB = { A: "FF38A169", B: "FFED8936", C: "FFE53E3E" };
      ws.eachRow((row, n) => {
        if (n === 1) return;
        const cell = row.getCell("segment");
        const argb = ARGB[cell.value];
        if (argb) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb } };
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.alignment = { horizontal: "center" };
        }
      });
      ws.views = [{ state: "frozen", ySplit: 1 }];
      ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: ws.columns.length } };

      const sm = wb.addWorksheet("Summary");
      sm.getColumn(1).width = 30;
      sm.getColumn(2).width = 26;
      const period = meta?.start && meta?.end ? `${meta.start} to ${meta.end}` : "Last 12 months";
      [
        ["Supplier", supplier],
        ["Retailer", String(retailer || "").toUpperCase()],
        ["Period", period],
        ["Total sales (R)", Math.round(meta?.total || 0)],
        ["Articles", rows.length],
        [],
        ["Band", "Articles · share of sales"],
        [SEGMENT_TITLE.A, `${bands.A?.count || 0} · ${fmtPct(bands.A?.share)}`],
        [SEGMENT_TITLE.B, `${bands.B?.count || 0} · ${fmtPct(bands.B?.share)}`],
        [SEGMENT_TITLE.C, `${bands.C?.count || 0} · ${fmtPct(bands.C?.share)}`],
        [],
        ["Rule", "Articles sorted biggest first. A = together the first 80% of sales, B = the next 15%, C = the last 5% and anything with no sales."],
        ["Scope", "Stores on the Excluded list are left out. An article switched off is counted up to that day."],
      ].forEach((r) => sm.addRow(r));
      sm.getCell("A7").font = { bold: true };
      sm.getCell("B7").font = { bold: true };
      sm.getCell("A8").font = { bold: true, color: { argb: ARGB.A } };
      sm.getCell("A9").font = { bold: true, color: { argb: ARGB.B } };
      sm.getCell("A10").font = { bold: true, color: { argb: ARGB.C } };

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      const stamp = meta?.end || new Date().toISOString().slice(0, 10);
      link.download = `ABC_${supplier}_${String(retailer || "").toUpperCase()}_${stamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("ABC export error:", error);
      toast({ title: "Download failed", description: "Could not build the ABC file.", status: "error", duration: 3000, isClosable: true });
    } finally {
      setDownloading(false);
    }
  };

  const isActive = (key) => view === key;

  return (
    <Box w="100%" h="100%" position="relative" display="flex" flexDirection="column" minH={0}>
      {/* header: title · view buttons · download · info */}
      <Flex justify="space-between" align="center" mb={2} gap={2} wrap="wrap">
        <Heading size="xs" color="gray.600" fontWeight="600">
          ABC SKU Segmentation – Last 12 Months
        </Heading>
        <Flex align="center" gap={2}>
          <ButtonGroup size="xs" isAttached variant="outline">
            {VIEWS.map((v) => (
              <Button
                key={v.key}
                onClick={() => changeView(v.key)}
                bg={isActive(v.key) ? "#1D2F3A" : "white"}
                color={isActive(v.key) ? "white" : "#4A5D6B"}
                borderColor="#CBD6DE"
                _hover={{ bg: isActive(v.key) ? "#1D2F3A" : "#F6F8FA" }}
                fontWeight="600"
                px={2.5}
              >
                {v.label}
              </Button>
            ))}
          </ButtonGroup>
          {view === "all" && (
            <IconButton
              aria-label="Download Excel"
              title="Download the full list as Excel"
              icon={<DownloadIcon />}
              size="xs"
              variant="outline"
              borderColor="#CBD6DE"
              color="#4A5D6B"
              isLoading={downloading}
              isDisabled={!rows.length}
              onClick={download}
            />
          )}
          <Tooltip
            label="Last 12 months of sales per article, biggest first. Green (A) = the articles that together make the first 80% of sales. Orange (B) = the next 15%. Red (C) = the last 5% and anything with no sales. Stores on the Excluded list are left out; an article switched off is counted up to that day."
            hasArrow
            placement="left"
            maxW="260px"
            whiteSpace="normal"
          >
            <InfoIcon w={3.5} h={3.5} cursor="pointer" color="gray.400" />
          </Tooltip>
        </Flex>
      </Flex>

      {/* legend chips (click = jump to that band in the All view) · period */}
      <Flex align="center" justify="space-between" gap={2} mb={2} wrap="wrap">
        <Flex gap={1.5} wrap="wrap">
          {["A", "B", "C"].map((seg) => {
            const b = bands[seg] || { count: 0, share: 0 };
            const clickable = view === "all" && b.count > 0;
            return (
              <Flex
                key={seg}
                align="center"
                gap={1.5}
                px={2}
                py="2px"
                borderRadius="999px"
                bg="#F6F8FA"
                border="1px solid #E2E9EE"
                cursor={clickable ? "pointer" : "default"}
                onClick={clickable ? () => jumpToBand(seg) : undefined}
                title={SEGMENT_TITLE[seg]}
                _hover={clickable ? { borderColor: SEGMENT_COLORS[seg] } : undefined}
              >
                <Box w="8px" h="8px" borderRadius="2px" bg={SEGMENT_COLORS[seg]} />
                <Text fontSize="11px" color="#4A5D6B" fontWeight="700" lineHeight="1.4">
                  {seg}
                </Text>
                <Text fontSize="11px" color="#64798A" lineHeight="1.4">
                  {b.count} {b.count === 1 ? "article" : "articles"} · {fmtPct(b.share)}
                </Text>
              </Flex>
            );
          })}
        </Flex>
        {meta?.start && meta?.end && (
          <Text fontSize="10.5px" color="#93A5B1" whiteSpace="nowrap">
            {meta.start} to {meta.end} · {fmtShort(meta.total)} total
          </Text>
        )}
      </Flex>

      {/* body */}
      {view === "all" ? (
        <Box flex="1" minH={0} display="flex" flexDirection="column" fontFamily="Inter, sans-serif">
          <Flex px={2} py="3px" fontSize="10px" color="#93A5B1" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" gap={2}>
            <Text w="26px" textAlign="right" flexShrink={0}>
              #
            </Text>
            <Box w="8px" flexShrink={0} />
            <Text flex="1" minW={0}>
              Article
            </Text>
            <Text w="84px" textAlign="right" flexShrink={0}>
              Sales
            </Text>
            <Text w="52px" textAlign="right" flexShrink={0}>
              Share
            </Text>
            <Text w="56px" textAlign="right" flexShrink={0}>
              Running
            </Text>
          </Flex>
          <Box ref={listRef} flex="1" minH={0} overflowY="auto" position="relative" border="1px solid #E2E9EE" borderRadius="8px">
            {rows.map((r, i) => {
              const first = i === 0 || rows[i - 1].segment !== r.segment;
              const b = bands[r.segment] || {};
              return (
                <React.Fragment key={r.articlecode || `${r.sku}-${i}`}>
                  {first && (
                    <Flex
                      ref={(el) => {
                        bandRefs.current[r.segment] = el;
                      }}
                      position="sticky"
                      top={0}
                      zIndex={1}
                      align="center"
                      gap={2}
                      px={2}
                      py="4px"
                      bg="#F6F8FA"
                      borderBottom="1px solid #E2E9EE"
                      fontSize="11px"
                      fontWeight="700"
                      color={SEGMENT_COLORS[r.segment]}
                    >
                      <Box w="8px" h="8px" borderRadius="2px" bg={SEGMENT_COLORS[r.segment]} />
                      {SEGMENT_TITLE[r.segment]}
                      <Text as="span" color="#64798A" fontWeight="500">
                        — {b.count || 0} {b.count === 1 ? "article" : "articles"} · {fmtPct(b.share)} of sales
                      </Text>
                    </Flex>
                  )}
                  <Flex align="center" gap={2} px={2} py="3px" fontSize="11.5px" borderBottom="1px solid #EDF2F5" _hover={{ bg: "#FAFCFD" }}>
                    <Text w="26px" textAlign="right" color="#93A5B1" flexShrink={0}>
                      {r.rank}
                    </Text>
                    <Box w="8px" h="8px" borderRadius="50%" bg={SEGMENT_COLORS[r.segment]} flexShrink={0} />
                    <Text flex="1" minW={0} color="#33454F" noOfLines={1} title={r.sku}>
                      {r.sku}
                      {r.active === false && (
                        <Text as="span" ml={1} color="#93A5B1" fontStyle="italic">
                          (inactive{r.inactive_since ? ` since ${r.inactive_since}` : ""})
                        </Text>
                      )}
                    </Text>
                    <Text w="84px" textAlign="right" color="#1D2F3A" fontWeight="600" flexShrink={0}>
                      {fmtR(r.value)}
                    </Text>
                    <Text w="52px" textAlign="right" color="#4A5D6B" flexShrink={0}>
                      {fmtPct(r.share)}
                    </Text>
                    <Text w="56px" textAlign="right" color="#93A5B1" flexShrink={0}>
                      {fmtPct(r.cumulative)}
                    </Text>
                  </Flex>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      ) : (
        <div ref={containerRef} style={{ width: "100%" }} />
      )}

      {!loading && rows.length === 0 && (
        <Text fontSize="sm" color="#93A5B1" textAlign="center" py={8}>
          No sales in the last 12 months
        </Text>
      )}

      {loading && <Spinner position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" />}
    </Box>
  );
};

export default Chart10;
